import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Bookmark, BookmarkCheck, ExternalLink, RefreshCw, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScoreBadge } from './ScoreBadge';
import {
  type ColumnDef,
  SortableHeader,
  filterItems,
  renderColumnFilter,
  sortItems,
  usePersistedColumnFilters,
  usePersistedColumnVisibility,
} from './tenderTableUtils';
import { TenderTableToolbar } from './TenderTableToolbar';
import { type TenderResult, tenderKey, tenderScore } from './types';

const FILTER_STORAGE_KEY = 'atek-tenders-column-filters';
const VISIBILITY_STORAGE_KEY = 'atek-tenders-column-visibility';
const PAGE_SIZE = 50;

interface TendersTableProps {
  tenders: TenderResult[];
  isLoading: boolean;
  isError: boolean;
  isRefreshing?: boolean;
  onRefresh: () => void;
  onRowClick: (t: TenderResult) => void;
  onToggleBookmark: (t: TenderResult) => void;
  onExclude: (t: TenderResult) => void;
  onRestore: (t: TenderResult) => void;
  isTenderSaved: (t: TenderResult) => boolean;
  isTenderExcluded: (t: TenderResult) => boolean;
  excludedCount: number;
  showExcluded: boolean;
  onToggleShowExcluded: () => void;
}

export function TendersTable({
  tenders,
  isLoading,
  isError,
  isRefreshing,
  onRefresh,
  onRowClick,
  onToggleBookmark,
  onExclude,
  onRestore,
  isTenderSaved,
  isTenderExcluded,
  excludedCount,
  showExcluded,
  onToggleShowExcluded,
}: TendersTableProps) {
  const [sortKey, setSortKey] = useState<string | null>('score');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);
  const [page, setPage] = useState(0);

  const sourceOptions = useMemo(() => {
    const set = new Set<string>();
    for (const t of tenders) if (t.source) set.add(t.source);
    return Array.from(set).sort().map((s) => ({ label: s, value: s }));
  }, [tenders]);

  const columns: ColumnDef<TenderResult>[] = useMemo(() => ([
    {
      key: 'score',
      label: 'Score',
      sortKey: 'score',
      filterType: 'number-range',
      getValue: (t) => tenderScore(t),
      render: (t) => <ScoreBadge score={tenderScore(t)} />,
      headerClassName: 'w-[90px]',
    },
    {
      key: 'name',
      label: 'Title',
      sortKey: 'name',
      filterType: 'text',
      getValue: (t) => t.name,
      render: (t) => (
        <div className="max-w-sm">
          <p className="font-medium text-xs break-words">{t.name}</p>
          {t.summary && <p className="text-xs text-muted-foreground break-words mt-0.5">{t.summary}</p>}
        </div>
      ),
    },
    {
      key: 'organisationName',
      label: 'Organisation',
      sortKey: 'organisationName',
      filterType: 'text',
      getValue: (t) => t.organisationName ?? '',
      render: (t) => <span className="text-sm">{t.organisationName ?? '—'}</span>,
    },
    {
      key: 'source',
      label: 'Source',
      filterType: 'select',
      filterOptions: sourceOptions,
      getValue: (t) => t.source,
      render: (t) => <Badge variant="outline" className="text-xs">{t.source}</Badge>,
    },
    {
      key: 'publishDate',
      label: 'Published',
      sortKey: 'publishDate',
      filterType: 'date-range',
      getValue: (t) => t.publishDate ?? '',
      render: (t) => <span className="text-sm text-muted-foreground whitespace-nowrap">{t.publishDate ?? '—'}</span>,
    },
    {
      key: 'deadline',
      label: 'Deadline',
      sortKey: 'deadline',
      filterType: 'date-range',
      getValue: (t) => t.deadline ?? '',
      render: (t) => <span className="text-sm text-muted-foreground whitespace-nowrap">{t.deadline ?? '—'}</span>,
    },
    {
      key: 'actions',
      label: 'Actions',
      noFilter: true,
      align: 'right',
      getValue: () => '',
      headerClassName: 'w-[110px] text-right',
      render: (t) => (
        <div className="flex items-center justify-end gap-0.5">
          {isTenderExcluded(t) ? (
            <button
              className="p-1 rounded text-muted-foreground/40 hover:text-green-600 transition-colors"
              title="Restore tender"
              onClick={(e) => { e.stopPropagation(); onRestore(t); }}
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              className="p-1 rounded text-muted-foreground/20 hover:text-red-500 transition-colors"
              title="Hide tender"
              onClick={(e) => { e.stopPropagation(); onExclude(t); }}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            className={cn(
              'p-1 rounded transition-colors',
              isTenderSaved(t)
                ? 'text-yellow-600 hover:text-yellow-700'
                : 'text-muted-foreground/30 hover:text-yellow-600',
            )}
            title={isTenderSaved(t) ? 'Already saved' : 'Save tender'}
            onClick={(e) => { e.stopPropagation(); onToggleBookmark(t); }}
          >
            {isTenderSaved(t) ? <BookmarkCheck className="h-3.5 w-3.5" /> : <Bookmark className="h-3.5 w-3.5" />}
          </button>
          <a
            href={t.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-blue-600 hover:text-blue-800 p-1"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      ),
    },
  ]), [sourceOptions, isTenderSaved, isTenderExcluded, onExclude, onRestore, onToggleBookmark]);

  const {
    columnFilters, setColumnFilter, clearColumnFilters, hasActiveColumnFilters,
  } = usePersistedColumnFilters(FILTER_STORAGE_KEY);

  const { toggleColumn, isVisible } = usePersistedColumnVisibility(VISIBILITY_STORAGE_KEY, columns);

  const visibleColumns = useMemo(() => columns.filter((c) => c.noFilter || isVisible(c.key)), [columns, isVisible]);

  const filteredItems = useMemo(
    () => filterItems(tenders, columns, columnFilters),
    [tenders, columns, columnFilters],
  );

  const sortedItems = useMemo(
    () => sortItems(filteredItems, columns, sortKey, sortDirection),
    [filteredItems, columns, sortKey, sortDirection],
  );

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages - 1);
  const pagedItems = useMemo(
    () => sortedItems.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE),
    [sortedItems, currentPage],
  );

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection((d) => (d === 1 ? -1 : 1));
    } else {
      setSortKey(key);
      setSortDirection(-1);
    }
    setPage(0);
  };

  const showDismissedToggle = excludedCount > 0 ? (
    <button
      className={cn(
        'text-[10px] transition-colors',
        showExcluded ? 'text-foreground underline' : 'text-muted-foreground hover:text-foreground',
      )}
      onClick={onToggleShowExcluded}
    >
      {showExcluded ? 'Hide dismissed' : `${excludedCount} hidden`}
    </button>
  ) : null;

  return (
    <div className="border rounded-lg overflow-hidden">
      <TenderTableToolbar
        items={sortedItems}
        columns={columns}
        csvFilename="tenders"
        hasActiveColumnFilters={hasActiveColumnFilters}
        onClearColumnFilters={clearColumnFilters}
        isVisible={isVisible}
        toggleColumn={toggleColumn}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
        countLabel={`Showing ${sortedItems.length} of ${tenders.length}`}
        extra={showDismissedToggle}
      />
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              {visibleColumns.map((col) => renderColumnFilter(col, columnFilters, setColumnFilter))}
            </TableRow>
            <TableRow>
              {visibleColumns.map((col) => (
                <TableHead
                  key={col.key}
                  className={cn(col.align === 'right' && 'text-right', col.headerClassName)}
                >
                  {col.sortKey ? (
                    <SortableHeader
                      label={col.label}
                      sortKey={col.sortKey}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                      align={col.align}
                    />
                  ) : (
                    col.label
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 8 }).map((_, i) => (
              <TableRow key={`skeleton-${i}`}>
                {visibleColumns.map((col) => (
                  <TableCell key={col.key}><Skeleton className="h-4 w-full" /></TableCell>
                ))}
              </TableRow>
            ))}
            {!isLoading && isError && (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="py-8 text-center">
                  <p className="text-red-600">Failed to load tenders. Is the crawlers service running on port 3100?</p>
                  <Button variant="outline" size="sm" className="mt-2" onClick={onRefresh}>
                    <RefreshCw className="h-3 w-3 mr-1" /> Retry
                  </Button>
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && pagedItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-muted-foreground">
                  {tenders.length === 0
                    ? 'No tender results yet. Run crawlers to fetch tenders.'
                    : 'No tenders match the filters'}
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && pagedItems.map((t, i) => (
              <TableRow
                key={`${t.source}-${tenderKey(t)}-${i}`}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onRowClick(t)}
              >
                {visibleColumns.map((col) => (
                  <TableCell
                    key={col.key}
                    className={cn(
                      'text-sm align-top',
                      col.align === 'right' && 'text-right tabular-nums',
                      col.className,
                    )}
                  >
                    {col.render ? col.render(t) : (String(col.getValue(t) ?? '') || '—')}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {totalPages > 1 && (
        <div className="border-t px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {currentPage + 1} of {totalPages}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(currentPage - 1)} disabled={currentPage === 0}>
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(currentPage + 1)} disabled={currentPage >= totalPages - 1}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
